import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";

import { loginScope, msalConfig } from "./utils/authentication/authConfig";
import configureAppStore from "./store/ConfigureStore";
import { App } from "./App";
import "./index.css";
import theme from "./styles/mui/theme";
import { IS_LOCALHOST, updateVariables } from "./constants/Config";
import { loadInsights } from "utils/logHandlers/appInsight";
import { GetEnvVariables } from "utils";

export let msalInstance: PublicClientApplication;
export let loginRequest: any;

const store = configureAppStore();

const container = document.getElementById("root")!;
const root = createRoot(container);

const AppWithAuthentication = () => {
  const [config, setConfig] = useState<any>();
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  const variables = useMemo<Record<string, any>>(() => {
    const envVariables = {
      REACT_APP_TEMPO_APPINSIGHTS_CONNECTION_STRING:"[_APP_INSIGHTS_CONN_]",
      REACT_APP_TEMPO_CLIENT_ID: "[_APP_CLIENT_ID_]",
      REACT_APP_TEMPO_LOGIN_URL: "[_APP_LOGIN_URL_]",
      REACT_APP_TEMPO_CONFIG_URL_REFERRAL:"[_APP_URL_FOR_CONFIG_API_]",
    };
    return IS_LOCALHOST ? GetEnvVariables() as unknown as Record<string, any> : envVariables;
  }, []);
  useEffect(() => {
    (async () => {
      if (!config) {
        setConfig(variables);
        updateVariables(variables);
        loadInsights(variables.REACT_APP_TEMPO_APPINSIGHTS_CONNECTION_STRING);
        msalInstance = new PublicClientApplication(msalConfig(variables));
        await msalInstance.initialize();
        msalInstance.enableAccountStorageEvents();

        const searchCode = new URLSearchParams(window.location.search).get("code");
        const hashCode = new URLSearchParams(window.location.hash.replace("#", "")).get("code");
        const authCode = searchCode || hashCode;

        let redirectResult = null;
        if (authCode) {
          const codeHandledKey = `msal_auth_code_handled_${authCode}`;
          if (!sessionStorage.getItem(codeHandledKey)) {
            sessionStorage.setItem(codeHandledKey, "true");
            redirectResult = await msalInstance.handleRedirectPromise();
          }
        } else {
          redirectResult = await msalInstance.handleRedirectPromise();
        }

        if (redirectResult?.account) {
          msalInstance.setActiveAccount(redirectResult.account);
        }

        const existingAccounts = msalInstance.getAllAccounts();
        if (existingAccounts.length > 0 && !msalInstance.getActiveAccount()) {
          msalInstance.setActiveAccount(existingAccounts[0]);
        }

        msalInstance.addEventCallback((event) => {
          if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
            const loginResult: any = event.payload;
            if (loginResult?.account) {
              msalInstance.setActiveAccount(loginResult.account);
            }
          }
        });
        loginRequest = loginScope(variables.REACT_APP_TEMPO_CLIENT_ID);
        setIsConfigLoaded(true);
      }
    })();
  }, []);

  return (
    <div>
      {isConfigLoaded ? (
        <React.StrictMode>
          <MsalProvider instance={msalInstance}>
            <Provider store={store}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <App />
              </ThemeProvider>
            </Provider>
          </MsalProvider>
        </React.StrictMode>
      ) : (
        <>Loading...</>
      )}
    </div>
  );
};

root.render(<AppWithAuthentication />);
